import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../billing/shared';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const Box = ({ title, hint, children, testid }) => (
  <div className="space-y-2 rounded-lg border border-slate-200 p-4" data-testid={testid}>
    <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
    {children}
  </div>
);

const SlotRow = ({ s, i, defaultCap, onChange, onRemove }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`rules-slot-${i}`}>
    <input type="time" className={`${inputCls} !w-32`} value={s.start} onChange={(e) => onChange({ start: e.target.value })} aria-label="Start" data-testid={`rules-slot-start-${i}`} />
    <span className="text-slate-400">to</span>
    <input type="time" className={`${inputCls} !w-32`} value={s.end} onChange={(e) => onChange({ end: e.target.value })} aria-label="End" data-testid={`rules-slot-end-${i}`} />
    <input type="number" min={1} max={50} className={`${inputCls} !w-24`} placeholder={`${defaultCap}`} value={s.cap} onChange={(e) => onChange({ cap: e.target.value })} title="Bookings allowed in this slot (blank = default)" aria-label="Slot limit" data-testid={`rules-slot-cap-${i}`} />
    <span className="text-[11px] text-slate-500">bookings</span>
    <button type="button" onClick={onRemove} className="rounded p-1.5 text-rose-600 hover:bg-rose-50" aria-label="Remove slot" data-testid={`rules-slot-remove-${i}`}><Trash2 size={14} /></button>
  </div>
);

const toForm = (r) => ({
  slots: r.windows.map((w) => ({ start: w.slice(0, 5), end: w.slice(6), cap: r.window_caps[w] ? String(r.window_caps[w]) : '' })),
  default_cap: String(r.default_cap), open_days: r.open_days, held: r.held, ppf_cap: String(r.ppf_cap), ppf_services: r.ppf_services,
});

export const BookingRules = () => {
  const { authHeader } = useAuth();
  const [f, setF] = useState(null);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/appointments/booking-rules`, { headers: authHeader() })
      .then(({ data }) => { setF(toForm(data)); setServices(data.services); }).catch((e) => toast.error(formatErr(e)));
  }, [authHeader]);
  if (!f) return <Loader2 className="animate-spin text-slate-400" />;

  const win = (s) => `${s.start}-${s.end}`;
  const windows = f.slots.map(win);
  const setSlot = (i, patch) => setF({ ...f, slots: f.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const toggleDay = (d) => setF({ ...f, open_days: f.open_days.includes(d) ? f.open_days.filter((x) => x !== d) : [...f.open_days, d] });
  const toggleHeld = (d, w) => {
    const cur = f.held[String(d)] || [];
    setF({ ...f, held: { ...f.held, [String(d)]: cur.includes(w) ? cur.filter((x) => x !== w) : [...cur, w] } });
  };
  const toggleSvc = (s) => setF({ ...f, ppf_services: f.ppf_services.includes(s) ? f.ppf_services.filter((x) => x !== s) : [...f.ppf_services, s] });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        windows, default_cap: Number(f.default_cap), open_days: f.open_days, ppf_cap: Number(f.ppf_cap), ppf_services: f.ppf_services,
        window_caps: Object.fromEntries(f.slots.filter((s) => s.cap).map((s) => [win(s), Number(s.cap)])),
        held: Object.fromEntries(Object.entries(f.held).map(([d, ws]) => [d, ws.filter((w) => windows.includes(w))])),
      };
      const { data } = await axios.put(`${API}/appointments/booking-rules`, body, { headers: authHeader() });
      setF(toForm(data)); toast.success('Booking rules saved');
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="booking-rules">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-teal-700">Booking rules</h2>
        <p className="mt-1 text-xs text-slate-500">Used by your website booking page, the staff booking form, the week calendar and the morning slot email. Lowering a limit never cancels existing bookings; the slot just stops taking new ones.</p>
      </div>
      <Box title="Time slots" hint="Leave a slot's limit blank to use the default." testid="rules-slots">
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">Default bookings per slot
          <input type="number" min={1} max={50} className={`${inputCls} !w-24`} value={f.default_cap} onChange={(e) => setF({ ...f, default_cap: e.target.value })} data-testid="rules-default-cap" />
        </label>
        {f.slots.map((s, i) => <SlotRow key={i} s={s} i={i} defaultCap={f.default_cap} onChange={(p) => setSlot(i, p)} onRemove={() => setF({ ...f, slots: f.slots.filter((_, j) => j !== i) })} />)}
        <button type="button" onClick={() => setF({ ...f, slots: [...f.slots, { start: '', end: '', cap: '' }] })} className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700" data-testid="rules-slot-add"><Plus size={13} /> Add time slot</button>
      </Box>
      <Box title="Open days" testid="rules-days">
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d, i) => (
            <label key={d} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${f.open_days.includes(i) ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-300 text-slate-500'}`}>
              <input type="checkbox" className="sr-only" checked={f.open_days.includes(i)} onChange={() => toggleDay(i)} data-testid={`rules-day-${i}`} /> {d}
            </label>
          ))}
        </div>
      </Box>
      <Box title="Held slots" hint="Tick a slot to keep it free every week on that day (for example a Friday prayer or team meeting)." testid="rules-held">
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead><tr><th className="pr-3 text-left font-semibold text-slate-500">Slot</th>{f.open_days.slice().sort().map((d) => <th key={d} className="px-2 font-semibold text-slate-600">{DAYS[d]}</th>)}</tr></thead>
            <tbody>
              {windows.filter((w) => /^\d{2}:\d{2}-\d{2}:\d{2}$/.test(w)).map((w) => (
                <tr key={w}>
                  <td className="pr-3 font-mono text-slate-700">{w}</td>
                  {f.open_days.slice().sort().map((d) => (
                    <td key={d} className="px-2 text-center"><input type="checkbox" checked={(f.held[String(d)] || []).includes(w)} onChange={() => toggleHeld(d, w)} data-testid={`rules-held-${d}-${w}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>
      <Box title="Weekly PPF limit" hint="The most bookings per week (Mon–Sun) for the services ticked below. 0 means no limit applies." testid="rules-ppf">
        <input type="number" min={0} max={100} className={`${inputCls} !w-24`} value={f.ppf_cap} onChange={(e) => setF({ ...f, ppf_cap: e.target.value })} data-testid="rules-ppf-cap" />
        <div className="flex flex-wrap gap-2">
          {services.map((s) => (
            <label key={s} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700">
              <input type="checkbox" checked={f.ppf_services.includes(s)} onChange={() => toggleSvc(s)} data-testid={`rules-ppf-svc-${s}`} /> {s}
            </label>
          ))}
        </div>
      </Box>
      <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="rules-save">
        {saving && <Loader2 size={14} className="animate-spin" />} Save booking rules
      </button>
    </section>
  );
};
